import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';

import { HStack, VStack } from '@/components/Layout';
import StepperNavigation from '@/components/Navigation/StepperNavigation';
import AddStepperStepTitleCard from '@/components/Stepper/AddStepperStepTitleCard';
import DockAddStepperAction from '@/components/Stepper/DockAddStepperAction';
import { toast } from '@/components/ui/use-toast';
import { handleApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';

import {
  Step1Creation,
  Step2Name,
  Step3Info,
  Step4PurposeRole,
  Step5Structure,
  Step6People,
} from '../-components/steps';
import { useOrgUnitForm } from '../-orgUnitWizard/hooks/useOrgUnitForm';
import { useUnitDetailPrefill } from '../-orgUnitWizard/hooks/useUnitDetailPrefill';
import { useWizardNavigator } from '../-orgUnitWizard/hooks/useWizardNavigator';
import { buildAddUnitCommand } from '../-orgUnitWizard/transforms';
import { useRestructuringUnitOptions } from '../-orgUnitWizard/view-model';
import { useDepartmentRestructuringSkeletonQuery } from '../-query';
import { useDepartmentUnitDetailQuery } from '../-query/departmentUnit';

import {
  type PostDepartmentUnitRequest,
  usePostDepartmentUnit,
} from './-query';

export const Route = createFileRoute('/_staff/organization/plan/$id/add/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t, i18n } = useTranslation(defaultNS, {
    keyPrefix: 'pages.organization.plan.addOrEditOrgUnit',
  });

  const STEP_LABELS = [
    t('step1AddingProcess.title'),
    t('step2OrgUnitName.title'),
    t('step3OrgUnitInfo.title'),
    t('step4PurposeRole.title'),
    t('step5Structure.title'),
    t('step6People.title'),
  ] as const;
  const { id } = useParams({ from: '/_staff/organization/plan/$id/add/' });
  const navigate = useNavigate();

  const {
    state,
    send,
    setCreationMode,
    setSourceUnitId,
    setAbolishSource,
    setName,
    setShortName,
    setUnitId,
    setOffice,
    setBusinessCardName,
    setDescription,
    setFunctionalRole,
    setMission,
    setHierarchyLevel,
    setOrderInHierarchy,
    setParentUnitId,
    setChildUnitIds,
    setPersons,
    setResponsible,
    setSubResponsible,
  } = useOrgUnitForm('add');

  const { locationHash, previousPage, nextPage, goto } = useWizardNavigator(
    state.context.step,
    send,
  );

  const { mutateAsync: saveUnit, isPending: isSaving } =
    usePostDepartmentUnit(id);

  const { data: planDetail } = useDepartmentRestructuringSkeletonQuery(id);
  const isEditable = planDetail?.status === 'InProgress';

  const { flatOptions, childOptions } = useRestructuringUnitOptions(
    id,
    state.context.parentUnitId,
  );
  const disabled = !state.can({ type: 'NEXT' }) || isSaving || !isEditable;

  const handleNext = () => {
    if (nextPage) {
      goto(nextPage);
    }
  };

  const buildPayload = (): PostDepartmentUnitRequest => {
    if (!planDetail) throw new Error('plan detail not loaded');
    return buildAddUnitCommand(state.context, planDetail.version);
  };

  const handleSave = async () => {
    if (!isEditable) return;
    try {
      await saveUnit(buildPayload());

      navigate({ to: `/organization/plan/${id}` });
    } catch (e) {
      const { message } = handleApiError(e, { language: i18n.language });
      toast({ description: message, variant: 'destructive' });
    }
  };

  const sections = STEP_LABELS.map((label, index) => ({
    label,
    hash: (index + 1).toString(),
  }));

  // TanStack Query で取得した `unitDetail` を XState マシンの context へ流し込むための橋渡し。
  const shouldFetchSource =
    state.context.creationMode === 'duplicate' &&
    Boolean(state.context.sourceUnitId);

  const { data: duplicateUnitDetail } = useDepartmentUnitDetailQuery(
    id,
    state.context.sourceUnitId,
    shouldFetchSource,
  );

  useUnitDetailPrefill({
    unitDetail: duplicateUnitDetail,
    setters: {
      setName,
      setShortName,
      setUnitId,
      setHierarchyLevel,
      setOrderInHierarchy,
      setParentUnitId,
      setChildUnitIds,
      setPersons,
      setResponsible,
      setSubResponsible,
      setBusinessCardName,
    },
  });

  return (
    <HStack className="h-[calc(100vh-64px)] w-full items-start gap-9 p-10">
      <StepperNavigation
        link={{ hash: locationHash, to: `/organization/plan/${id}/add` }}
        title={t('addTitle')}
        sections={sections}
      />

      <VStack className="h-full min-w-0 flex-1 items-start gap-6">
        {/* Step title */}
        <AddStepperStepTitleCard
          stepName={`${locationHash}. ${STEP_LABELS[Number(locationHash) - 1]}`}
          className="w-full"
        />

        {/* Form card (ステップごとに表示を切替) */}
        {locationHash === '1' && (
          <Step1Creation
            creationMode={state.context.creationMode}
            sourceUnitId={state.context.sourceUnitId}
            abolishSource={state.context.abolishSource}
            setCreationMode={setCreationMode}
            setSourceUnitId={setSourceUnitId}
            setAbolishSource={setAbolishSource}
            flatOptions={flatOptions}
          />
        )}

        {locationHash === '2' && (
          <Step2Name
            name={state.context.name}
            shortName={state.context.shortName}
            businessCardName={state.context.businessCardName || ''}
            setName={setName}
            setShortName={setShortName}
            setBusinessCardName={setBusinessCardName}
          />
        )}

        {locationHash === '3' && (
          <Step3Info
            unitId={state.context.unitId || ''}
            office={state.context.office || ''}
            setUnitId={setUnitId || (() => {})}
            setOffice={setOffice || (() => {})}
          />
        )}

        {locationHash === '4' && (
          <Step4PurposeRole
            description={state.context.description}
            functionalRole={state.context.functionalRole}
            mission={state.context.mission}
            setDescription={setDescription}
            setFunctionalRole={setFunctionalRole}
            setMission={setMission}
          />
        )}

        {locationHash === '5' && (
          <Step5Structure
            hierarchyLevel={state.context.hierarchyLevel}
            setHierarchyLevel={(v) => setHierarchyLevel(v)}
            orderInHierarchy={state.context.orderInHierarchy}
            setOrderInHierarchy={setOrderInHierarchy}
            parentUnitId={state.context.parentUnitId}
            setParentUnitId={setParentUnitId}
            childUnitIds={state.context.childUnitIds}
            setChildUnitIds={setChildUnitIds}
            flatOptions={flatOptions}
            childOptions={childOptions}
          />
        )}

        {locationHash === '6' && (
          <Step6People
            planId={id}
            responsible={state.context.responsible ?? null}
            subResponsible={state.context.subResponsible ?? null}
            persons={state.context.persons}
            onResponsibleChange={(p) =>
              (setResponsible || (() => {}))(p ?? undefined)
            }
            onSubResponsibleChange={(p) =>
              (setSubResponsible || (() => {}))(p ?? undefined)
            }
            onPersonsChange={setPersons}
          />
        )}

        {/* Action dock */}
        <div className="mb-32 w-full">
          <DockAddStepperAction
            baseLink={`/organization/plan/${id}/add`}
            previousPage={previousPage}
            nextPage={nextPage}
            nextComplete={!nextPage}
            disabled={disabled}
            onNextClick={handleNext}
            onCompleteClick={handleSave}
          />
        </div>
      </VStack>
    </HStack>
  );
}
